import { calculateChangeTracking } from '../../Core/ChangeTracker';
import { actions } from '../WorksheetEditor';
import { callApiGetWorksheet, callApiPutWorksheet } from '../WorksheetEditor/apiCalls';
import { catchWithAuthErrors, addErrorNotice } from '../../Helpers/rxjsHelpers';
import { concatLatest } from '../../../Lib/Rxjs/operators';
import { RemoveArrayEntryAction, SyncAction } from './types';
import { Epic, ofType } from 'redux-observable';
import { mergeMap, filter } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { IDataSyncState } from './types';
import { IDataSyncModel } from '../../ViewModels/IDataSyncModel';
import copy from 'fast-clone';
import { RootState } from 'Data/Redux';
import * as iop from 'Lib/Object/Path/Immutable';
import isEmptyObject from 'Lib/Object/isEmptyObject';
import { actions as noticeActions } from '../Notices';
import { readOnlySelector } from '../../Core/readOnlySelector';

/*
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 * These epics are pretending to be standalone because we want the data sync to be its own separate state eventually.
 * They are actually just working for the worksheet editor for now.
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */

export const triggerSync: Epic = (action$) =>
  action$.pipe(
    ofType(actions.applySnapshot.toString()),
    mergeMap(() => of(actions.sync()))
  );

export const triggerSyncByFlag: Epic = (action$) =>
  action$.pipe(
    ofType(
      // On sync completion, run again in case changes were made during the sync.
      actions.syncResolve.toString(),

      actions.syncErrorResolve.toString(),

      // Whenever a value changes
      actions.setValue.toString(),
      actions.copyValue.toString(),
      actions.patchValues.toString(),
      actions.addArrayEntry.toString()
    ),
    filter(({ payload: { sync = false } = {} }) => !!sync),
    mergeMap(() => of(actions.sync()))
  );

export const removeArrayElement: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.removeArrayEntry.toString()),
    mergeMap((action: RemoveArrayEntryAction) => {
      const {
        payload: { name, sync },
      } = action;
      const state: RootState = state$.value;
      const array = iop.get<Array<any>>(state.worksheetEditor.syncModel, name);
      const someEntryIsBeingCreated = Array.isArray(array) && array.some((v) => isEmptyObject(v));

      // TODO: Fix this in the future. Model types and using array ids will help here.
      //
      // Don't let the user delete an array element while one is being created.
      // Strange issues can happen in conflcit resolution. If another user has created an item, and you quickly create
      // and then delete an item, it can delete the other user's object.
      // Caused by the client not identifying array entries by id, so we don't know what the id to be deleted should be,
      // we just use array index.
      // For now we just block delete until create is finished, so everything has an id.
      if (someEntryIsBeingCreated) {
        return of(
          noticeActions.addNotice({
            message:
              'You cannot remove an entry while another is being created. Please try again after saving has finished.',
            severity: 'warning',
          })
        );
      } else if (sync) {
        return of(actions.sync());
      } else {
        return EMPTY;
      }
    })
  );

/**
 * This is our main sync epic responsible for keeping the server up-to-date with clients changes.
 * It runs like a service, processing actions 1 at a a time, updating the server state to look like the client state.
 */
export const sync: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.sync.toString()),
    concatLatest((action: SyncAction) => {
      const stateValue: RootState = state$.value;
      const force = action.payload?.force ?? false;
      const target: IDataSyncState = stateValue.worksheetEditor;
      const worksheetId = stateValue.worksheetEditor.worksheetId;
      const worksheetTypeName: string = stateValue.worksheetEditor.worksheetType.WorksheetType;

      const {
        syncStatus,
        syncModel: clientModelBefore,
        syncModel_synced: serverModelBefore,
        syncParams = {},
        syncParams_synced: syncedsyncParams = {},
      } = target;

      // A sync will be performed once the conflict/syncError is resolved so do nothing.
      if (syncStatus === 'unresolvedConflict' || syncStatus === 'syncError') {
        // See the concateLatest() docs, must return a value so it does not remain blocked.
        return of(actions.syncConflictEarlyExit());
      }

      const hasChanges = calculateChangeTracking(clientModelBefore, serverModelBefore).changeCount > 0;
      const hasSyncParams = calculateChangeTracking(syncParams, syncedsyncParams).changeCount > 0;

      const shouldExitEarly = !force && !hasSyncParams && !hasChanges;
      if (shouldExitEarly) return of(actions.syncResolve());

      // If there are pending changes, do a PUT before the final GET, otherwise skip straight to the GET
      const callApiPutOrGetData = (updateModel?: object) => {
        return updateModel && !readOnlySelector(stateValue)
          ? callApiPutWorksheet(worksheetTypeName, { Body: updateModel }, { worksheetId })
          : callApiGetWorksheet(worksheetTypeName, worksheetId);
      };

      const serverModelSent = hasChanges ? copy(clientModelBefore) : undefined;

      const handleConflictError = (error: any) => {
        const serverModelSentWithBase = {
          ...serverModelSent,
          syncConflictBaseData: (serverModelBefore as IDataSyncModel).data,
        };

        // Attempt to resolve the conflict
        return callApiPutOrGetData(serverModelSentWithBase).pipe(
          mergeMap((serverModelReceived: IDataSyncModel) => {
            if (serverModelReceived.hasSyncConflict) {
              // If the user changes the worksheet/page, the current worksheet may not be the one we're trying to sync.
              // This check can be removed if we store worksheets by id, instead of just one worksheet at a time.
              const currentWorksheetId = state$.value.worksheetEditor.syncModel.id;
              if (currentWorksheetId !== worksheetId) {
                return of(
                  addErrorNotice('Failed to update worksheet. A conflicting update could not be resolved.', error),
                  actions.syncResolve({ syncPatch: { syncParams }, sync: false })
                );
              } else {
                return of(actions.syncConflict({ syncPatch: { clientModelBefore, serverModelReceived } }));
              }
            } else {
              return of(
                actions.syncResolve({
                  syncPatch: { serverModelSent, serverModelReceived, clientModelBefore, syncParams },
                  sync: true,
                })
              );
            }
          }),
          catchWithAuthErrors((error: any) => handleGeneralError(error))
        );
      };

      const handleSyncError = (error: any) => {
        // If the user changes the worksheet/page, the current worksheet may not be the one we're trying to sync.
        // This check can be removed if we store worksheets by id, instead of just one worksheet at a time.
        const currentWorksheetId = state$.value.worksheetEditor.syncModel.id;
        if (currentWorksheetId !== worksheetId) {
          return of(
            addErrorNotice('Failed to update worksheet.', error),
            actions.syncResolve({ syncPatch: { syncParams }, sync: false })
          );
        } else {
          return of(actions.syncError({ syncPatch: { clientModelBefore, serverModelReceived: serverModelBefore } }));
        }
      };

      const handleGeneralError = (error: any) =>
        // Something went wrong, attempt a repair (perform a GET and force state back to those results)
        callApiPutOrGetData().pipe(
          mergeMap((serverModelReceived: any) =>
            // Repair succeeded, still report error, reset all state to server state.
            of(
              addErrorNotice('Failed to update worksheet.', error),
              actions.syncResolve({
                syncPatch: { serverModelReceived, syncParams },
                sync: false,
              })
            )
          ),
          catchWithAuthErrors((recoveryError: any) =>
            // Repair failed, report additional error for failure and reset client state to last _known_ server state.
            of(
              addErrorNotice('Failed to update worksheet.', error),
              addErrorNotice(
                'Something went really wrong when attempting to retrieve the worksheet. Try refreshing the page.',
                recoveryError
              ),
              actions.syncResolve({ syncPatch: { syncParams }, sync: false })
            )
          )
        );

      return callApiPutOrGetData(serverModelSent).pipe(
        // Run the sync and update our copy of the server model to what is received
        mergeMap((serverModelReceived: any) =>
          of(
            actions.syncResolve({
              syncPatch: {
                clientModelBefore,
                serverModelSent: serverModelSent || serverModelBefore,
                serverModelReceived,
                syncParams,
              },
              sync: true,
            })
          )
        ),
        catchWithAuthErrors((error: any) => {
          if (error.code === 409 /* HTTP Conflict */) {
            return handleConflictError(error);
          } else if (error.code === 460 /* Internet Disconnected - Internally Defined Error Code */) {
            return handleSyncError(error);
          } else {
            return handleGeneralError(error);
          }
        })
      );
    })
  );

export const syncConflictResolve: Epic = (action$) =>
  action$.pipe(
    ofType(actions.syncConflictResolve.toString()),
    mergeMap(() => of(actions.syncResolve({ sync: true })))
  );
